import {
  getTrackingCartItemPurchaseMode,
  getTrackingPromotionLabel,
} from '@/feature/cart/services/cart.tracking'
import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { trackingConsentAtom } from '@/lib/tracking/services/tracking.store'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  type ProductCard_ProductPriceAndStockFragment,
  type ProductType,
  VisibleCardsAnalyticsByReferencesDocument,
  VisibleCardsPriceAndStockByReferencesDocument,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'

gql(/* GraphQL */ `
  query VisibleCardsAnalyticsByReferences($references: [String!]!) {
    productsByReferences(references: $references) {
      reference
      parentReference
      name
      type
      brand {
        name
        urlKey
      }
      analytics {
        promotionStatus
      }
      breadcrumbs {
        name
      }
      nomenclatureFamily
      nomenclatureGroup
    }
  }
`)

gql(/* GraphQL */ `
  query VisibleCardsPriceAndStockByReferences($references: [String!]!) {
    productsByReferences(references: $references) {
      reference
      ...ProductCard_ProductPriceAndStock
    }
  }
`)

type AnalyticsProduct = ProductCard_ProductPriceAndStockFragment & {
  reference: string
  parentReference?: string | null
  name?: string | null
  type?: ProductType
  brand?: { name?: string | null } | null
  breadcrumbs?: { name?: string | null }[] | null
  nomenclatureFamily?: string | null
  nomenclatureGroup?: string | null
}

type VisibleCardsOptions = {
  itemListName?: string
  products?: (Pick<AnalyticsProduct, 'reference' | 'parentReference' | 'name' | 'type' | 'brand'> &
    Partial<ProductCard_ProductPriceAndStockFragment> &
    Partial<Pick<AnalyticsProduct, 'breadcrumbs'>>)[]
  scrollableRoot?: HTMLElement | null
}

export function useVisibleProductCards(options?: VisibleCardsOptions) {
  const [visibleProductCards, setVisibleProductCards] = useState<string[]>([])
  const [productCardsRefs, setProductCardsRefs] = useState<
    React.RefObject<HTMLDivElement | null>[]
  >([])

  const observerRef = useRef<IntersectionObserver | null>(null)
  const seenReferenceIdsRef = useRef<Set<string>>(new Set())
  const pushedReferenceIdsRef = useRef<Set<string>>(new Set())

  const isAuthenticated = useIsAuthenticated()
  const { config } = useSiteConfig()

  const handleIntersect: IntersectionObserverCallback = (entries) => {
    const newlyVisibleReferences: string[] = []

    for (const entry of entries) {
      if (!entry.isIntersecting) continue

      const element = entry.target as HTMLElement
      const reference = element.dataset.productId
      if (!reference) continue

      if (!seenReferenceIdsRef.current.has(reference)) {
        seenReferenceIdsRef.current.add(reference)
        newlyVisibleReferences.push(reference)
      }

      observerRef.current?.unobserve(entry.target)
    }

    if (newlyVisibleReferences.length > 0) {
      setVisibleProductCards(Array.from(seenReferenceIdsRef.current))
    }
  }

  useEffect(() => {
    if (!productCardsRefs || productCardsRefs.length === 0) {
      return
    }

    if (observerRef.current) {
      const prevObserver = observerRef.current
      prevObserver.disconnect()
    }

    const newObserver = new IntersectionObserver(handleIntersect, {
      root: options?.scrollableRoot ?? null,
      rootMargin: '0px',
      threshold: 0.1,
    })
    observerRef.current = newObserver

    // Observe all available current nodes
    productCardsRefs.forEach((ref) => {
      const node = ref?.current

      if (node) {
        newObserver.observe(node)
      }
    })

    return () => {
      newObserver.disconnect()
    }
  }, [productCardsRefs, options?.scrollableRoot])

  const references = visibleProductCards
  const hasProvidedProducts = !!options?.products && options.products.length > 0

  const isAnalyticsEnabled = useAtomValue(trackingConsentAtom)
  // Local query for analytics enrichment (product info)

  const { data: analyticsData, loading: analyticsLoading } = useQuery(
    VisibleCardsAnalyticsByReferencesDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { references },

      skip: references.length === 0 || hasProvidedProducts || isSSR,
    },
  )

  // Separate cache-first query for price and stock data
  // Use cache-first to allow fetching if not in cache, while still preferring cache when available
  const { data: priceAndStockData, loading: priceAndStockLoading } = useQuery(
    VisibleCardsPriceAndStockByReferencesDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { references },
      skip: references.length === 0 || hasProvidedProducts || isSSR,
      fetchPolicy: 'cache-and-network', //! TODO REMOVE
    },
  )

  const analyticsByRef = useMemo(() => {
    const referenceToProductMap = new Map<string, AnalyticsProduct>()

    if (!isAnalyticsEnabled) return referenceToProductMap

    // If products are provided, use them directly
    if (hasProvidedProducts && options?.products) {
      options.products.forEach((product) => {
        if (product?.reference) {
          referenceToProductMap.set(product.reference, product as AnalyticsProduct)
        }
      })
    } else {
      // Merge analytics and price/stock data from queries
      analyticsData?.productsByReferences?.forEach((product) => {
        if (product?.reference) {
          const priceAndStock = priceAndStockData?.productsByReferences?.find(
            (ps) => ps?.reference === product.reference,
          )
          const merged = {
            ...product,
            ...priceAndStock,
          }
          referenceToProductMap.set(product.reference, merged as unknown as AnalyticsProduct)
        }
      })
    }

    return referenceToProductMap
  }, [analyticsData, priceAndStockData, hasProvidedProducts, options?.products, isAnalyticsEnabled])

  // Analytics: push view_item_list in batches of 3 for newly visible products
  useEffect(() => {
    // Skip loading check if products are provided directly
    if (!hasProvidedProducts && (analyticsLoading || priceAndStockLoading)) return
    if (references.length === 0) return

    const newlyVisibleUnpushedReferences = visibleProductCards.filter(
      (reference) => !pushedReferenceIdsRef.current.has(reference),
    )
    if (newlyVisibleUnpushedReferences.length === 0) return

    // When products are provided, track all products that have analytics data
    // When products are not provided, filter only products that have price and stock data available in cache
    const referencesWithData = hasProvidedProducts
      ? newlyVisibleUnpushedReferences.filter((reference) => {
          const analyticsProduct = analyticsByRef.get(reference)

          return !!analyticsProduct
        })
      : newlyVisibleUnpushedReferences.filter((reference) => {
          const analyticsProduct = analyticsByRef.get(reference)

          return (
            analyticsProduct &&
            (analyticsProduct.price !== undefined ||
              analyticsProduct.bestArticlePrice !== undefined ||
              analyticsProduct.stock?.availability !== undefined)
          )
        })

    // If no products have data yet, wait
    if (referencesWithData.length === 0) return

    const isPublicPriceEnabled = config?.catalog?.isPublicPricesEnabled ?? false

    const analyticsItems = referencesWithData
      .map((reference) => {
        const analyticsProduct = analyticsByRef.get(reference)
        if (!analyticsProduct) return null
        const positionIndex = productCardsRefs.findIndex(
          (r) => r.current?.dataset.productId === reference,
        )
        const position = positionIndex >= 0 ? positionIndex + 1 : ''

        const priceNumber =
          (analyticsProduct?.price?.exclTax && analyticsProduct.price.exclTax > 0
            ? analyticsProduct.price.exclTax
            : undefined) ??
          (analyticsProduct?.bestArticlePrice || 0)

        return {
          item_id: reference,
          item_code_p: analyticsProduct.parentReference || '',
          item_nature: (analyticsProduct.type || 'article').toLowerCase(),
          item_variants_amount: '',
          item_name: analyticsProduct.name || '',
          item_price: priceNumber || 0,
          item_index: String(position),
          item_brand: analyticsProduct.brand?.name || '',
          item_category: analyticsProduct.nomenclatureFamily || '',
          item_category2: analyticsProduct.nomenclatureGroup || '',
          item_category3: '',
          item_list_name: options?.itemListName || '',
          item_promotion: getTrackingPromotionLabel(analyticsProduct?.analytics?.promotionStatus),
          item_public_price: isPublicPriceEnabled && !isAuthenticated ? 'true' : 'false',
          item_purchase_mode: getTrackingCartItemPurchaseMode(
            analyticsProduct?.stock?.availability || [],
            analyticsProduct?.canAddToCart || false,
            isAuthenticated,
          ),
        }
      })
      .filter((x): x is NonNullable<typeof x> => !!x)

    if (analyticsItems.length === 0) return

    for (let i = 0; i < analyticsItems.length; i += 3) {
      const chunk = analyticsItems.slice(i, i + 3)
      const viewItemListEvent = {
        event: 'view_item_list' as const,
        ecommerce: {
          currency: 'EUR' as const,
          items: chunk,
        },
      }

      pushToGtmDataLayer(viewItemListEvent)
    }

    // Mark the references as pushed
    referencesWithData.forEach((reference) => pushedReferenceIdsRef.current.add(reference))
  }, [
    visibleProductCards,
    options?.itemListName,
    analyticsByRef,
    analyticsLoading,
    priceAndStockLoading,
    references.length,
    productCardsRefs,
    isAuthenticated,
    config,
    isAnalyticsEnabled,
    hasProvidedProducts,
  ])

  return { visibleProductCards, setVisibleProductCards, productCardsRefs, setProductCardsRefs }
}
